import { useState } from "react";
import { Input } from "./ui/input";

const BTN = "px-2 py-1 rounded text-[11px] border transition-colors";

/** One candidate for a rain-out credit: credit now, skip for now, or no credit. */
export const CreditDecisionRow = ({ player, decision, note, onDecide, onNote }) => {
  const [showBasis, setShowBasis] = useState(false);
  const basis = player.credit_basis || "Full session rate — no discount on this child's fees.";
  if (player.already_credited) {
    return (
      <div className="bg-slate-900/40 border border-emerald-700/40 rounded px-2 py-1.5 flex items-center gap-2"
        data-testid={`rainout-candidate-${player.player_id}`}>
        <span className="text-xs text-slate-300 flex-1 min-w-0 truncate">{player.player_name}</span>
        <span className="text-[10px] text-emerald-300 shrink-0 font-mono"
          data-testid={`rainout-credited-${player.player_id}`}>
          credited ${Number(player.credited_amount || 0).toFixed(2)}
        </span>
      </div>
    );
  }

  const states = [
    ["credit", "Credit", "border-teal-500 bg-teal-500/20 text-teal-200"],
    ["later", "Skip for now", "border-sky-500 bg-sky-500/20 text-sky-200"],
    ["none", "No credit", "border-amber-500 bg-amber-500/20 text-amber-200"],
  ];

  return (
    <div className="bg-slate-900/70 border border-slate-700 rounded px-2 py-1.5"
      data-testid={`rainout-candidate-${player.player_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs min-w-0 truncate max-w-[55%] sm:max-w-none sm:flex-1 ${
          decision === "credit" ? "text-slate-100" : "text-slate-400"}`}>
          {player.player_name}
        </span>
        <button type="button" onClick={() => setShowBasis((v) => !v)}
          title={basis}
          className="font-mono text-xs text-teal-300 underline decoration-dotted underline-offset-2 shrink-0"
          data-testid={`rainout-amount-${player.player_id}`}>
          ${player.credit_amount}
        </button>
        {player.payment_unverified && (
          <span className="text-[10px] text-rose-300 shrink-0"
            title={player.payment_evidence || "Nothing collected against their invoice"}
            data-testid={`rainout-unpaid-${player.player_id}`}>
            invoiced — $0 collected
          </span>
        )}
        {player.rsvp_attending === false && (
          <span className="text-[10px] text-amber-300 shrink-0" data-testid={`rainout-rsvp-${player.player_id}`}>
            said not coming
          </span>
        )}
        {player.rsvp_attending === true && (
          <span className="text-[10px] text-emerald-300 shrink-0">was coming</span>
        )}
        {player.prior_decision === "pending" && (
          <span className="text-[10px] text-sky-300 shrink-0" data-testid={`rainout-was-skipped-${player.player_id}`}>
            skipped last time
          </span>
        )}
        {player.prior_decision === "no_credit" && (
          // Starts set to "no credit" from last time. Say so, or the admin
          // presses Credit and nothing happens for this player.
          <span
            className="text-[10px] text-rose-300 shrink-0"
            title={player.prior_reason || "Refused last time"}
            data-testid={`rainout-was-refused-${player.player_id}`}
          >
            refused last time — tap Credit to change
          </span>
        )}
        <div className="flex gap-1 shrink-0">
          {states.map(([key, label, active]) => (
            <button
              key={key}
              type="button"
              onClick={() => onDecide(key)}
              className={`${BTN} ${decision === key ? active : "border-slate-700 text-slate-500 hover:text-slate-300"}`}
              data-testid={`rainout-${key}-${player.player_id}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {showBasis && (
        <p className="mt-1 text-[10px] text-slate-400" data-testid={`rainout-basis-${player.player_id}`}>
          {basis}
        </p>
      )}
      {decision === "none" && (
        <Input
          className="mt-1.5 h-8 text-[11px] bg-slate-950 border-slate-700"
          placeholder="Why no credit? (kept on the record)"
          value={note}
          onChange={(e) => onNote(e.target.value)}
          data-testid={`rainout-skip-note-${player.player_id}`}
        />
      )}
      {decision === "later" && (
        <p className="mt-1 text-[10px] text-sky-300/80">
          Parked — they&apos;ll come up again on the &ldquo;still to decide&rdquo; list.
        </p>
      )}
    </div>
  );
};
